import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

import Icon from 'components/icon/Icon';
import { Button } from 'components/ui/button';
import useAuthStore from 'store/auth';
import { cn } from 'lib/utils';
import PersonalDetailEdit from './PersonalDetailEdit';
import { captureUserActivity } from 'services/profile/api';
import useUpdateProfile from 'hooks/useUpdateProfile';
import { USER_ACTIVITY } from '../../../types';
import DeleteAccountDialog from 'components/dialogs/DeleteAccountDialog';
import { useDeleteFamilyAttributes } from 'services/profile/hooks';

export default function BasicDetails({
  setLoading,
}: {
  setLoading: (loading: boolean) => void;
}) {
  const { t } = useTranslation();

  const userAttributes = useAuthStore((store) => store.userAttributes);
  const isAdmin = useAuthStore((store) => store.isCurrentUserAdmin);
  const userEmail = useAuthStore((store) => store.userEmail);
  const currentUserProfileId = useAuthStore(
    (store) => store.currentUserProfileId
  );
  const memberDetails = useAuthStore((store) => store.memberDetails);
  const logout = useAuthStore((store) => store.logout);

  const { onUpdateProfile, onUpdateMemberDetails } = useUpdateProfile();

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);

  const { mutate, isPending } = useDeleteFamilyAttributes({
    onSuccess: async () => {
      await captureUserActivity(
        isAdmin ? USER_ACTIVITY.DELETE_ACCOUNT : USER_ACTIVITY.DELETE_PROFILE
      );

      if (isAdmin) {
        toast.success(t('account_deleted_msg'));
        logout();
        return;
      }

      toast.success(t('member_deleted_msg'));

      const adminProfileId = memberDetails.find(
        (member) => member.relation === 'Admin'
      )?.profile_id;
      if (adminProfileId) {
        await onUpdateMemberDetails();
        await onUpdateProfile(adminProfileId);
      }
    },
  });

  const basicDetails = [
    { label: t('gender'), value: userAttributes?.gender, tw: 'capitalize' },
    { label: t('dob'), value: userAttributes?.birthdate },
    {
      label: t('height'),
      value: userAttributes?.height
        ? `${userAttributes?.height} ${userAttributes?.height_unit}`
        : '',
    },
    {
      label: t('weight'),
      value: userAttributes?.weight
        ? `${userAttributes?.weight} ${userAttributes?.weight_unit}`
        : '',
      tw: 'uppercase',
    },
    { label: t('email'), value: userEmail },
  ];

  if (isEdit) {
    return (
      <PersonalDetailEdit
        onClose={() => setIsEdit(false)}
        setLoading={setLoading}
      />
    );
  }

  const onDelete = () => {
    if (isAdmin) {
      setOpenDelete(true);
      return;
    }

    mutate({ profile_ids: [currentUserProfileId] });
  };

  const onCloseDelete = () => setOpenDelete(false);

  const onAccountDelete = () => {
    mutate({ profile_ids: [currentUserProfileId] });
  };

  return (
    <>
      <div className='flex flex-col flex-1 h-full mx-2 my-6 text-white'>
        <div>
          <div className='flex items-center justify-between pl-2 border-b border-b-primary'>
            <h2 className='font-medium text-primary'>{t('basic_details')}</h2>
            <button
              className='px-2 py-1 bg-transparent rounded-sm hover:bg-white/5'
              onClick={() => setIsEdit(true)}
            >
              <Icon icon='edit' size={16} color='#10A4B1' />
            </button>
          </div>

          <dl className='px-2 py-3 space-y-2'>
            {basicDetails.map(({ label, value, tw }, index: number) => (
              <div
                className='flex items-center justify-between'
                key={`${value}-${index}`}
              >
                <dt className='font-medium text-gray-400'>{label}</dt>
                <dd className={cn('w-1/2 text-right truncate', tw)}>
                  {value || '-'}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <div className='flex flex-col items-center justify-center flex-1 h-full mt-8 space-y-4'>
          <Button
            size='sm'
            className='px-8 bg-rose-400 hover:bg-rose-400/80'
            disabled={isPending}
            onClick={onDelete}
          >
            {isPending && (
              <Loader2
                role='progressbar'
                className='w-4 h-4 mr-2 animate-spin'
              />
            )}
            {isAdmin ? t('delete_account') : t('delete_profile')}
          </Button>
          <Button
            size='sm'
            className='bg-blue-600 hover:bg-blue-600/80'
            disabled={isPending}
            onClick={logout}
          >
            {t('sign_out')}
          </Button>
        </div>
      </div>

      <DeleteAccountDialog
        open={openDelete}
        onClose={onCloseDelete}
        onDelete={onAccountDelete}
        isLoading={isPending}
      />
    </>
  );
}
